import { checkPermissions, permissionsByEntity, useAuth } from '@cmg/auth';
import { useCallback, useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router';

import routeFactory from '../../../common/util/routeFactory';
import type { TabValue } from '../components/calendar-tabs/CalendarTabs.model';

const getTabRoutesConfig = (userPermissions: string[]) => {
  const canReadIoi = checkPermissions(userPermissions, [permissionsByEntity.Ioi.READ]);
  const tabValueMap: Record<string, TabValue> = {
    [routeFactory.calendarLiveOfferings.routePath]: 'live',
    [routeFactory.calendarPricedOfferings.routePath]: 'priced',
    [routeFactory.calendarFiledOfferings.routePath]: 'filed',
    [routeFactory.calendarPostponedOfferings.routePath]: 'postponed',
    [routeFactory.calendarLockupExpirationOfferings.routePath]: 'lockupExpirations',
    [canReadIoi
      ? routeFactory.calendarMyOfferingsWithAllocation.routePath
      : routeFactory.calendarMyOfferings.routePath]: 'myOfferings',
  };

  return {
    tabValueMap,
    routeMatchPaths: Object.keys(tabValueMap),
  };
};
export const useCalendarTabs = () => {
  const { userPermissions } = useAuth();
  const { routeMatchPaths, tabValueMap } = useMemo(
    () => getTabRoutesConfig(userPermissions),
    [userPermissions]
  );

  const history = useHistory();
  const match = useRouteMatch({ path: routeMatchPaths });
  const activeTab = useMemo(() => {
    if (!match?.path) {
      return 'live';
    }

    return tabValueMap[match.path] ?? 'live';
  }, [match?.path, tabValueMap]);

  const handleTabChange = useCallback(
    (nextTab: TabValue) => {
      const [routePath] =
        Object.entries(tabValueMap).find(([_, tab]) => tab === nextTab) ??
        tabValueMap[routeFactory.calendarLiveOfferings.routePath];

      history.push(routePath);
    },
    [history, tabValueMap]
  );

  return [activeTab, handleTabChange] as const;
};
